import { ImageSquare, Lock, Paperclip, Trash } from '@phosphor-icons/react';
import { esImagen, type TicketFile } from '../services/api';
import { formatBytes } from './AdjuntosMensaje';
import styles from './ListaAdjuntos.module.css';

/** Lo que se sabe del mensaje que trajo cada adjunto, sacado del hilo. */
export interface OrigenAdjunto {
  autor: string | null;
  interno: boolean;
}

interface Props {
  files: TicketFile[];
  /** messageId → quién lo escribió y si era nota interna. */
  origen: Map<string, OrigenAdjunto>;
  /** Quien abrió el ticket, para los adjuntos del alta. */
  usuario: string | null;
  onAbrirImagen: (f: TicketFile) => void;
  onDescargar: (f: TicketFile) => void;
  /** Sin esto no hay papelera: el cliente no borra (K47) y el congelado
   *  tampoco (K53). */
  onBorrar?: (f: TicketFile) => void;
  ocupado?: boolean;
}

/**
 * Todos los adjuntos del ticket, en una línea cada uno (opción B del paso 34).
 *
 * Las miniaturas del hilo dicen QUÉ mensaje trajo cada cosa; esto contesta
 * «¿dónde está el log?» sin bajar por el hilo. Es también el único sitio desde
 * el que se borra un adjunto, ahora que la galería no existe.
 */
export function ListaAdjuntos({
  files, origen, usuario, onAbrirImagen, onDescargar, onBorrar, ocupado,
}: Props) {
  return (
    <ul className={styles.lista}>
      {files.map((f) => {
        const o = f.messageId ? origen.get(f.messageId) : undefined;
        const interno = Boolean(o?.interno);
        const quien = interno
          ? 'nota interna'
          : o?.autor ?? (f.messageId ? null : f.source === 'requester' ? usuario : 'Equipo');
        const meta = [formatBytes(f.sizeBytes), quien].filter(Boolean).join(' · ');
        const Icono = interno ? Lock : esImagen(f) ? ImageSquare : Paperclip;
        return (
          <li key={f.id} className={styles.fila}>
            <button
              type="button"
              className={styles.abrir}
              onClick={() => (esImagen(f) ? onAbrirImagen(f) : onDescargar(f))}
              title={`${esImagen(f) ? 'Ver' : 'Descargar'} ${f.filename}`}
            >
              <Icono size={13} weight={interno ? 'fill' : 'regular'} className={interno ? styles.interno : undefined} />
              <span className={styles.nombre}>{f.filename}</span>
              <span className={styles.meta}>{meta}</span>
            </button>
            {onBorrar && (
              <button
                type="button"
                className={styles.borrar}
                onClick={() => onBorrar(f)}
                disabled={ocupado}
                aria-label={`Borrar ${f.filename}`}
                title={`Borrar ${f.filename}`}
              >
                <Trash size={13} weight="regular" />
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}
